<template>
  <div class="page-category" data-t="provider-page">
    <StBreadCrumbs :crumbs="crumbs" />
    <div class="title-wrapper">
      <div class="title-group">
        <StIcon name="providers" size="32" class="icon" />
        <h3 class="category-title" data-t="category-title-nb0u">
          {{ pageTitle }}
          <span
            v-if="games"
            class="games-counter"
            data-t="games-counter-8fzs"
            >{{ games }}</span
          >
        </h3>
      </div>
      <div class="toggle-wrapper">
        <span class="toggle-label">{{ t('casino.showUnavailable') }}</span>
        <StToggle
          v-model="forbiddenByCountryOnly"
          class="toggle"
          :disabled="forbiddenByCountryDisabled"
        />
      </div>
      <SelectSorting v-model="sort" class="select" />
    </div>
    <GamesInfiniteList
      :request-params="paramsValue"
      show-games-counter
      :request-order-by="orderBy"
      @games-counter="setGamesCounter"
    />
    <PageTournamentCard v-if="tournament" :card="tournament" />
  </div>
</template>

<script lang="ts" setup>
import { useCasinoDeps } from '@st/casino/useDeps'

interface Props {
  providerCode: string
  pageTitle: string
}

const { providerCode, pageTitle } = defineProps<Props>()
const { t } = useI18n()
const games = ref(0)
const forbiddenByCountryOnly = ref<boolean>(false)
const forbiddenByCountryDisabled = ref<boolean>(false)
const paramsValue = computed(() => ({
  providerCode,
  allowedByCountryOnly: !forbiddenByCountryOnly.value,
}))

const { sort, orderBy } = useSelectSorting()

function setGamesCounter(val: number) {
  games.value = val
  if (val === 0) {
    forbiddenByCountryOnly.value = true
    forbiddenByCountryDisabled.value = true
  }
}

const crumbs = [
  { label: t('breadCrumbs.main'), link: '/' },
  { label: t('breadCrumbs.casino'), link: '/casino' },
  { label: t('breadCrumbs.providers'), link: '/casino/providers' },
]

const { casinoTournament: tournament, PageTournamentCard } = useCasinoDeps()
</script>

<style scoped>
.page-category {
  height: 100%;
  margin-bottom: var(--spacing-600);

  .select {
    flex-shrink: 1;
    width: 170px;
  }
}

.title-wrapper {
  display: flex;
  gap: var(--spacing-150);
  align-items: center;
  align-self: stretch;
  justify-content: space-between;

  width: 100%;
  margin-top: var(--spacing-300);
  margin-bottom: var(--spacing-300);
}

.icon {
  color: var(--text-tertiary);
}

.category-title {
  display: flex;
  gap: var(--spacing-100);
  align-items: center;
  justify-content: center;

  margin: 0;

  font: var(--desktop-text-2xl-semibold);
}

.title-group {
  display: flex;
  flex-grow: 1;
  gap: var(--spacing-100);
  align-items: center;
}

.games-counter {
  display: inline-flex;
  gap: var(--spacing-0);
  align-items: center;
  justify-content: center;

  width: var(--spacing-350);
  padding: var(--spacing-025) var(--spacing-125) var(--spacing-025)
    var(--spacing-125);

  font: var(--desktop-text-xs-semibold);
  text-align: center;

  background: var(--background-tertiary);
  border-radius: var(--border-radius-full, 999px);
}

.toggle-wrapper {
  display: inline-flex;
  gap: var(--spacing-075);
  align-items: center;
}

.toggle-label {
  font: var(--desktop-text-xs-medium);
  white-space: nowrap;
}

.toggle {
  width: 50px;
}
</style>
